import { useEffect, useMemo } from 'react';
import { useParameterAddition } from '../../../shared/lib/hooks/useParameterAddition';
import { useBoltConfigurationApply } from '../../../shared/lib/hooks/useBoltConfigurationApply';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import { buildMappingRepairLogPlan } from '../lib/mappingRepairDiagnostics';
import { buildMappingRepairFailureProgress } from '../lib/mappingRepairFailureProgress';
import { infoMappingFlow, toTraceKey, traceMappingFlow } from '../lib/mappingTrace';
import {
  buildMappingRepairAggregateProgress,
  buildMappingRepairFallbackProgress,
  createActiveRepairProgressContext,
  getNextRepairProgressStepId,
  isParameterAdditionRepairStepId,
  type ActiveRepairProgressContext,
  type MappingRepairProgressStepId,
} from '../lib/mappingRepairProgress';
import { shouldStartMappingRepair } from '../lib/mappingRepairGate';
import { markMappingRepairPendingSave } from '../lib/mappingProfileProjectTrust';
import {
  createMappingRepairActions,
  runMappingRepairExecution,
} from '../lib/mappingRepairExecution';
import { waitForProgressWindowRequestEffect } from './useMappingParameterCatalogs';
import { useMappingRepairLifecycle } from './useMappingRepairLifecycle';
import type { OneShotMappingRepairInput } from './mappingRepairHookContracts';

export function useOneShotMappingRepair(input: OneShotMappingRepairInput) {
  const {
    activeUserId,
    enabled,
    autoRepair,
    projectKey,
    profileKey,
    modelVersion,
    menuConfig,
    connectorConfig,
    objectTypes,
    catalogReady,
    catalogLoading,
    repairableCount,
    repairableParameterNames,
    missingParameterNames,
    mappedParameterNames,
    processProjectParameterScope,
    repairProjectBindings,
    repairKinds,
    repairParameterCounts,
    familyRepairGroups,
    signature,
    attemptScope,
    resolveFamilyRepairKindsAfterClassification,
    verifyRepairParameters,
    onRepairInFlightChange,
  } = input;
  const {
    isAdding,
    parameterProgress,
    addParameters,
    addAdditionalCategoryParameters,
    addProcessAndProjectParameters,
    addMappedProjectParameters,
  } = useParameterAddition();
  const {
    apply: applyBoltConfigurationRepair,
    progressModel: boltRepairProgressModel,
  } = useBoltConfigurationApply();
  const {
    attemptedSignaturesRef,
    loggedGateKeysRef,
    repairingSignature,
    setRepairingSignature,
    activeRepairProgressContext,
    setActiveRepairProgressContext,
    repairStepProgress,
    setRepairStepProgress,
    activeParameterProgressStepId,
    setActiveParameterProgressStepId,
    repairError,
    setRepairError,
    currentSignatureRef,
    currentAttemptScopeRef,
    currentVerifyRepairParametersRef,
    latestParameterProgressRef,
    mountedRef,
    clearRepairAttempt,
    alreadyAttemptedRepair,
    claimRepair,
    releaseRepair,
  } = useMappingRepairLifecycle({
    signature,
    attemptScope,
    missingParameterNames,
    isAdding,
    parameterProgress,
    verifyRepairParameters,
    onRepairInFlightChange,
  });
  const pendingRepairProgressContext = useMemo<ActiveRepairProgressContext | null>(() => {
    const repairGate = {
      enabled,
      autoRepair,
      projectKey,
      catalogReady,
      catalogLoading,
      repairableCount,
      repairKindCount: repairKinds.length,
      alreadyAttempted: alreadyAttemptedRepair,
      repairInFlight: repairingSignature !== null,
    };

    if (!shouldStartMappingRepair(repairGate)) {
      return null;
    }

    return createActiveRepairProgressContext({
      signature,
      projectKey,
      modelVersion,
      repairableCount,
      repairableParameterNames,
      repairKinds,
      repairParameterCounts,
    });
  }, [
    alreadyAttemptedRepair,
    autoRepair,
    catalogLoading,
    catalogReady,
    enabled,
    modelVersion,
    projectKey,
    repairKinds,
    repairParameterCounts,
    repairableCount,
    repairableParameterNames,
    repairingSignature,
    signature,
  ]);

  useEffect(() => {
    const repairGate = {
      enabled,
      autoRepair,
      projectKey,
      catalogReady,
      catalogLoading,
      repairableCount,
      repairKindCount: repairKinds.length,
      alreadyAttempted: alreadyAttemptedRepair,
      repairInFlight: repairingSignature !== null,
    };
    const shouldStartRepair = shouldStartMappingRepair(repairGate);

    if (repairableCount > 0 || alreadyAttemptedRepair || catalogLoading) {
      // Ungated on purpose: this is the only record of WHY the repair did or
      // did not fire. Each distinct gate state is logged once per mount —
      // catalogLoading oscillates A/B/A during write bursts, and keying the
      // log on "state changed since last log" replayed the same states
      // hundreds of times.
      const gateKey = [
        signature,
        enabled,
        autoRepair,
        catalogReady,
        catalogLoading,
        repairableCount,
        alreadyAttemptedRepair,
        repairingSignature !== null,
        shouldStartRepair,
      ].join('::');
      if (!loggedGateKeysRef.current.has(gateKey)) {
        loggedGateKeysRef.current.add(gateKey);
        infoMappingFlow('mapping readiness repair gate evaluated', {
          ...repairGate,
          signatureKey: toTraceKey(signature),
          shouldStartRepair,
          repairKinds,
          mounted: mountedRef.current,
          currentSignatureKey: toTraceKey(currentSignatureRef.current),
        });
      }
    }

    if (!shouldStartRepair) {
      return;
    }

    // Re-check when claiming: another hook may have started after this effect's
    // render-time gate was calculated.
    if (!claimRepair()) {
      return;
    }
    const repairStartedAt = Date.now();
    const signatureKey = toTraceKey(signature);
    const connectorTarget = Object.freeze({
      destination: connectorConfig.parameterDestination,
      bindingKind: connectorConfig.parameterBindingKind,
    });
    const repairPlan = buildMappingRepairLogPlan({
      repairKinds,
      repairParameterCounts,
      repairableCount,
    });
    infoMappingFlow('mapping readiness repair start', {
      projectKey,
      signatureKey,
      repairKinds,
      repairableCount,
      missingParameterCount: missingParameterNames?.length ?? 0,
      familyRepairGroupCount: familyRepairGroups.length,
      repairParameterCounts,
      connectorTarget,
    });
    traceMappingFlow('mapping readiness repair plan', {
      projectKey,
      signatureKey,
      steps: repairPlan,
    });
    setRepairError((current) => (current?.signature === signature ? null : current));
    const progressContext = createActiveRepairProgressContext({
      signature,
      projectKey,
      modelVersion,
      repairableCount,
      repairableParameterNames,
      repairKinds,
      repairParameterCounts,
    });
    setActiveRepairProgressContext(progressContext);
    latestParameterProgressRef.current = null;
    setRepairStepProgress({
      stepId: progressContext.activeStepId,
      model: buildMappingRepairFallbackProgress(progressContext),
    });
    setRepairingSignature(signature);

    const isSameProjectRepair = () =>
      mountedRef.current && currentAttemptScopeRef.current === attemptScope;
    const isCurrentRepair = isSameProjectRepair;
    const retainFailureProgress = (message: string) => {
      setRepairStepProgress({
        stepId: null,
        model: buildMappingRepairFailureProgress(message, latestParameterProgressRef.current),
      });
    };
    const updateRepairStep = (
      stepId: MappingRepairProgressStepId,
      status: 'current' | 'complete' | 'error'
    ) => {
      if (!isSameProjectRepair()) {
        traceMappingFlow('mapping readiness repair step transition ignored', {
          projectKey,
          signatureKey,
          stepId,
          status,
          reason: 'repair scope changed',
          elapsedMs: Date.now() - repairStartedAt,
        });
        return;
      }

      traceMappingFlow('mapping readiness repair step transition', {
        projectKey,
        signatureKey,
        stepId,
        status,
        elapsedMs: Date.now() - repairStartedAt,
      });

      if (status === 'current') {
        latestParameterProgressRef.current = null;
        setActiveParameterProgressStepId(
          isParameterAdditionRepairStepId(stepId) ? stepId : null
        );
        setRepairStepProgress((current) =>
          current && current.stepId !== null && current.stepId !== stepId
            ? null
            : current
        );
      } else {
        setActiveParameterProgressStepId((current) =>
          current === stepId ? null : current
        );
        if (status === 'complete' && stepId !== 'verify') {
          setRepairStepProgress((current) =>
            current?.stepId === stepId ? null : current
          );
        }
      }

      setActiveRepairProgressContext((current) => {
        if (current?.signature !== signature) {
          return current;
        }

        const completedStepIds =
          status === 'complete' && !current.completedStepIds.includes(stepId)
            ? [...current.completedStepIds, stepId]
            : current.completedStepIds;
        const activeStepId =
          status === 'complete'
            ? getNextRepairProgressStepId(current, completedStepIds, stepId)
            : status === 'current' || status === 'error'
              ? stepId
              : current.activeStepId;

        return {
          ...current,
          activeStepId,
          completedStepIds,
          failedStepId: status === 'error' ? stepId : current.failedStepId,
        };
      });
    };

    const repair = async () => {
      await waitForProgressWindowRequestEffect();
      if (!isSameProjectRepair()) {
        return;
      }

      markMappingRepairPendingSave(activeUserId, projectKey, profileKey);
      traceMappingFlow('mapping repair persistence pending save', {
        projectKey,
        profileKey,
        signatureKey,
      });

      await runMappingRepairExecution({
        repairKinds,
        actions: createMappingRepairActions({
          addParameters: (options) => addParameters({ ...options, connectorTarget }),
          addAdditionalCategoryParameters,
          addProcessAndProjectParameters,
          processProjectParameterScope,
          repairProjectBindings,
          addMappedParameters: addMappedProjectParameters,
          applyBoltConfigurationRepair,
          projectKey,
          modelVersion,
          menuConfig,
          connectorConfig,
          objectTypes,
          resolveFamilyRepairKindsAfterClassification,
          isSameProjectRepair,
          setRepairStepProgress,
        }),
        progressCallbacks: {
          onStepStart: (stepId) => updateRepairStep(stepId, 'current'),
          onStepComplete: (stepId) => updateRepairStep(stepId, 'complete'),
          onStepError: (stepId) => updateRepairStep(stepId, 'error'),
        },
        projectKey,
        signature,
        familyRepairGroups,
        missingParameterNames,
        mappedParameterNames,
        mountedRef,
        currentSignatureRef,
        isCurrentRepair,
        updateRepairStep,
        retainFailureProgress,
        setRepairStepProgress,
        setRepairError,
        verifyRepairParameters: (actionResults) => currentVerifyRepairParametersRef.current(actionResults),
      });
    };

    void repair()
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Automatic project setup failed.';
        if (!isCurrentRepair()) {
          traceMappingFlow('mapping readiness repair stale error ignored', {
          projectKey,
          signatureKey: toTraceKey(signature),
          currentSignatureKey: toTraceKey(currentSignatureRef.current),
          mounted: mountedRef.current,
          signatureMatches: currentSignatureRef.current === signature,
          message,
        });
          return;
        }

        traceMappingFlow('mapping readiness repair error', {
          projectKey,
          signatureKey: toTraceKey(signature),
          message,
        });
        retainFailureProgress(message);
        setRepairError({
          signature,
          message,
        });
      })
      .finally(() => {
        releaseRepair();
        if (mountedRef.current) {
          setRepairingSignature((current) =>
            current === signature ? null : current
          );
        }
        traceMappingFlow('mapping readiness repair finalize', {
          projectKey,
          signatureKey,
          mounted: mountedRef.current,
          currentSignatureKey: toTraceKey(currentSignatureRef.current),
          elapsedMs: Date.now() - repairStartedAt,
        });
      });
  }, [
    addAdditionalCategoryParameters, activeUserId,
    addMappedProjectParameters, addParameters,
    addProcessAndProjectParameters, alreadyAttemptedRepair,
    autoRepair, applyBoltConfigurationRepair,
    catalogLoading, catalogReady, claimRepair,
    connectorConfig, currentAttemptScopeRef, currentSignatureRef,
    currentVerifyRepairParametersRef, enabled,
    familyRepairGroups, latestParameterProgressRef,
    loggedGateKeysRef, mappedParameterNames,
    menuConfig, missingParameterNames, modelVersion,
    mountedRef, objectTypes, onRepairInFlightChange,
    projectKey, profileKey, processProjectParameterScope,
    repairProjectBindings, repairParameterCounts, repairKinds,
    repairableCount, repairableParameterNames, releaseRepair,
    resolveFamilyRepairKindsAfterClassification,
    repairingSignature, setActiveParameterProgressStepId,
    setActiveRepairProgressContext, setRepairError,
    setRepairingSignature, setRepairStepProgress,
    signature, attemptScope, verifyRepairParameters,
  ]);

  const repairProgressContext = activeRepairProgressContext ?? pendingRepairProgressContext;
  const activeRepairAppliesToCurrentProject =
    repairProgressContext !== null &&
    repairProgressContext.projectKey === projectKey &&
    (
      repairingSignature === repairProgressContext.signature ||
      signature === repairProgressContext.signature
    );
  const activeRepairInFlight =
    activeRepairAppliesToCurrentProject &&
    repairingSignature === repairProgressContext?.signature;

  const operationProgressModel = useMemo<OperationProgressModel | null>(() => {
    if (!activeRepairAppliesToCurrentProject || !repairProgressContext) {
      return null;
    }

    const fallbackProgress = buildMappingRepairFallbackProgress({
      repairableCount: repairProgressContext.repairableCount,
      repairKinds: repairProgressContext.repairKinds,
    });
    const activeStepId = repairProgressContext.activeStepId;
    const parameterChildProgress =
      isAdding &&
      parameterProgress &&
      activeParameterProgressStepId === activeStepId
        ? parameterProgress
        : null;
    const boltChildProgress =
      activeStepId === 'bolt'
        ? boltRepairProgressModel
        : null;
    const scopedStepProgress =
      repairStepProgress &&
      (
        repairStepProgress.stepId === null ||
        repairStepProgress.stepId === activeStepId ||
        repairStepProgress.stepId === repairProgressContext.failedStepId
      )
        ? repairStepProgress.model
        : null;
    const childProgress =
      parameterChildProgress ?? boltChildProgress ?? scopedStepProgress ?? fallbackProgress;

    return buildMappingRepairAggregateProgress({
      context: repairProgressContext,
      childProgress,
      fallbackProgress,
    });
  }, [
    activeRepairAppliesToCurrentProject,
    activeParameterProgressStepId,
    boltRepairProgressModel,
    isAdding,
    parameterProgress,
    repairProgressContext,
    repairStepProgress,
  ]);

  return {
    clearRepairAttempt,
    hasAttemptedRepair: attemptedSignaturesRef.current.has(attemptScope),
    isRepairing: activeRepairInFlight,
    operationProgressModel,
    repairErrorMessage:
      repairError?.signature === signature ? repairError.message : null,
  };
}
